import { useMemo } from 'react'
import { Cpu, Radio, Router, Monitor } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import StatCard from './StatCard'
import DomainStatusSection from './DomainStatusSection'
import ActivityChart from './ActivityChart'
import ActiveSessions from './ActiveSessions'
import { useTenant } from '../../hooks/useTenant'
import { useRouters } from '../../hooks/useRouters'
import { useHosts } from '../../hooks/useHosts'

export default function Dashboard() {
  const { t } = useTranslation()
  const { data: tenant } = useTenant()
  const { data: routers } = useRouters()

  const { data: hosts } = useHosts()

  const { onlineRouters, offlineRouters } = useMemo(() => {
    const list = routers || []
    const online = list.filter((r) => r.status === 'Online').length
    return { onlineRouters: online, offlineRouters: list.length - online }
  }, [routers])

  const { onlineHosts, offlineHosts } = useMemo(() => {
    const STALE_MS = 5 * 60 * 1000
    const list = hosts || []
    const resolveStatus = (h) => {
      if (h?.status === 'Maintenance' || h?.status === 'Error') return h.status
      const handshakeOk = h?.vpnPeerLastHandshake &&
        (Date.now() - new Date(h.vpnPeerLastHandshake).getTime()) < STALE_MS
      const pingOk = h?.vpnPeerPingSuccess === true
      const reachable = h?.vpnPeerReachableViaVpn === true
      if (reachable && (handshakeOk || pingOk)) return 'Online'
      if (handshakeOk && pingOk) return 'Online'
      if (h?.status === 'Online' && handshakeOk) return 'Online'
      return 'Offline'
    }
    const online = list.filter((h) => resolveStatus(h) === 'Online').length
    return { onlineHosts: online, offlineHosts: list.length - online }
  }, [hosts])

  const offlineSuffix = t('dashboard.stats.offlineSuffix')

  const stats = [
    {
      name: t('dashboard.stats.activeDevices'),
      value: '0',
      subtitle: `0 ${offlineSuffix}`,
      icon: Cpu,
      color: 'primary',
      useThemedIconBox: true,
    },
    {
      name: t('dashboard.stats.gatewaysOnline'),
      value: '0',
      subtitle: `0 ${offlineSuffix}`,
      icon: Radio,
      color: 'success',
      useThemedIconBox: true,
    },
    {
      name: t('dashboard.stats.routersOnline'),
      value: String(onlineRouters),
      subtitle: `${offlineRouters} ${offlineSuffix}`,
      icon: Router,
      color: 'secondary',
      useThemedIconBox: true,
    },
    {
      name: t('dashboard.stats.activeHosts'),
      value: String(onlineHosts),
      subtitle: `${offlineHosts} ${offlineSuffix}`,
      icon: Monitor,
      color: 'primary',
      useThemedIconBox: true,
    },
  ]

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('dashboard.title')}</h1>
            <p className="mt-1 text-sm text-gray-600">
              {t('dashboard.subtitle')}
            </p>
          </div>
          {tenant && (
            <div className="flex items-center gap-3 px-4 py-2 bg-gray-50 rounded-lg border border-gray-200">
              <div className="icon-box w-10 h-10 font-semibold text-sm" style={{ background: 'var(--theme-icon-box-background, color-mix(in srgb, var(--theme-primary) 15%, transparent))' , color: 'var(--theme-icon-box-text, var(--theme-primary))' }}>
                {tenant.name
                  ? tenant.name
                      .split(/\s+/)
                      .map((w) => w[0])
                      .join('')
                      .substring(0, 2)
                      .toUpperCase()
                  : '??'}
              </div>
              <div>
                <div className="text-sm font-semibold text-gray-900">
                  {tenant.name || t('dashboard.tenantLoading')}
                </div>
                {tenant.slug && (
                  <div className="text-xs text-gray-500">
                    {tenant.slug}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          <StatCard key={stat.name} {...stat} />
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ActivityChart />
        <ActiveSessions />
      </div>

      {/* Domain Status */}
      <DomainStatusSection />
    </div>
  )
}
